import React, { useState, useEffect, useMemo, useRef, useDeferredValue } from 'react';
import {
  Dialog,
  DialogTitle,
  Typography,
  IconButton,
  Toolbar,
  Box,
  CircularProgress,
  LinearProgress,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  useMediaQuery,
} from '@mui/material';
import ViewerDialogContent from './ViewerDialogContent';
import ViewerDialogFrame from './ViewerDialogFrame';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  TableChart as TableChartIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  RestartAlt as RestartAltIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { fetchViewerFile, useViewerData } from './viewerHttp';
import useWheelZoom from './useWheelZoom';

// Theme colors
const primaryColor = '#5757b0';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '95vw',
    maxHeight: '90vh', // Reduced from 95vh to ensure it fits on screen
    width: '85vw',
    height: '80vh', // Reduced from 85vh to be more conservative
    backgroundColor: '#f5f5f5',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(87, 87, 176, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '& .MuiDialog-paper.MuiDialog-paperFullScreen': {
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    borderRadius: 0,
    margin: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
      margin: 0,
    },
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(87, 87, 176, 0.15)',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${primaryColor} 0%, #6b6bce 50%, #8080d7 100%)`,
  color: 'white',
  padding: '12px 20px',
  paddingTop: 'calc(12px + env(safe-area-inset-top))',
  paddingLeft: 'calc(20px + env(safe-area-inset-left))',
  paddingRight: 'calc(20px + env(safe-area-inset-right))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 700,
  fontSize: '1.1rem',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.25)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  '& .MuiTypography-root': {
    flex: 1,
    margin: 0,
    fontWeight: 600,
    fontSize: '1.1rem',
    letterSpacing: '0.025em',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  },
  '& > .MuiBox-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
  },
  '& > .MuiBox-root > .MuiTypography-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& > .MuiIconButton-root': {
    flexShrink: 0,
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    paddingTop: 'calc(10px + env(safe-area-inset-top))',
    paddingLeft: 'calc(12px + env(safe-area-inset-left))',
    paddingRight: 'calc(12px + env(safe-area-inset-right))',
    fontSize: '1rem',
    flexWrap: 'nowrap',
    gap: '8px',
    '& .MuiTypography-root': {
      fontSize: '1rem',
      minWidth: 0,
      wordBreak: 'break-word',
    },
  },
}));

const ToolbarContainer = styled(Toolbar)(({ theme }) => ({
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  minHeight: '48px !important',
  padding: '0 20px',
  justifyContent: 'space-between',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '0 12px',
    minHeight: '44px !important',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start',
  },
}));

const ExcelContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  flex: '1 1 auto',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#f8f9fa',
  overflow: 'hidden',
  padding: '4px',
  boxSizing: 'border-box',
  [theme.breakpoints.down('sm')]: {
    maxHeight: '100%',
    padding: '0',
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: '1px solid #e0e0e0',
  backgroundColor: 'white',
  '& .MuiTab-root': {
    textTransform: 'none',
    minWidth: 120,
    fontWeight: 500,
    color: '#666',
    '&.Mui-selected': {
      color: primaryColor,
    },
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiTab-root': {
      minWidth: 90,
      fontSize: '0.85rem',
    },
  },
  '& .MuiTabs-indicator': {
    backgroundColor: primaryColor,
  },
}));

const EXCEL_COLUMN_WIDTH = 200;
const EXCEL_ROW_HEADER_WIDTH = 64;
const EXCEL_HEADER_HEIGHT = 48;
const EXCEL_ROW_HEIGHT = 48;
const EXCEL_OVERSCAN_ROWS = 8;
const EXCEL_MIN_ZOOM = 0.5;
const EXCEL_MAX_ZOOM = 2;
const EXCEL_ZOOM_STEP = 0.1;

export const getExcelViewerLayout = (zoom, columnCount) => {
  const normalizedZoom = Number.isFinite(Number(zoom)) ? Number(zoom) : 1;
  const normalizedColumnCount = Math.max(Number(columnCount) || 0, 1);
  const columnWidth = Math.max(80, Math.round(EXCEL_COLUMN_WIDTH * normalizedZoom));
  const rowHeaderWidth = Math.max(40, Math.round(EXCEL_ROW_HEADER_WIDTH * normalizedZoom));
  const headerHeight = Math.max(24, Math.round(EXCEL_HEADER_HEIGHT * normalizedZoom));
  const rowHeight = Math.max(24, Math.round(EXCEL_ROW_HEIGHT * normalizedZoom));

  return {
    columnWidth,
    rowHeaderWidth,
    headerHeight,
    rowHeight,
    tableWidth: rowHeaderWidth + (normalizedColumnCount * columnWidth),
  };
};

const getExcelColumnLabel = (columnIndex) => {
  let remaining = columnIndex + 1;
  let label = '';

  while (remaining > 0) {
    const remainder = (remaining - 1) % 26;
    label = String.fromCharCode(65 + remainder) + label;
    remaining = Math.floor((remaining - 1) / 26);
  }

  return label;
};

const ExcelGridCell = ({
  children,
  header = false,
  rowHeader = false,
  title = '',
  width = EXCEL_COLUMN_WIDTH,
  height = EXCEL_ROW_HEIGHT,
  zoom = 1,
  cellStyle = {},
}) => (
  <div
    role={rowHeader ? 'rowheader' : header ? 'columnheader' : 'gridcell'}
    title={title || undefined}
    style={{
      boxSizing: 'border-box',
      width,
      height,
      minWidth: width,
      padding: `${Math.max(4, Math.round(12 * zoom))}px ${Math.max(6, Math.round(16 * zoom))}px`,
      borderRight: '1px solid #e0e0e0',
      borderBottom: header ? '2px solid #dee2e6' : '1px solid #e0e0e0',
      backgroundColor: header || rowHeader ? '#f8f9fa' : 'inherit',
      color: header ? '#495057' : 'inherit',
      fontSize: `${Math.max(header ? 0.65 : 0.625, (header ? 0.9 : 0.875) * zoom)}rem`,
      fontWeight: header ? 600 : 400,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      textAlign: rowHeader ? 'center' : 'left',
      position: rowHeader ? 'sticky' : undefined,
      left: rowHeader ? 0 : undefined,
      zIndex: rowHeader ? (header ? 4 : 3) : undefined,
      ...cellStyle,
    }}
  >
    {children}
  </div>
);

const ExcelViewer = ({ open, onClose, filename = '', eventId = null, filePath = '', embedded = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    data: excelData,
    loading,
    error,
  } = useViewerData({
    open,
    eventId,
    filePath,
    missingMessage: t('viewer.excel_missing_source', 'File id or path is required'),
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeSheet, setActiveSheet] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const containerRef = useRef(null);
  const tableContainerRef = useRef(null);
  const zoomAnchorRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(500);
  const [zoom, setZoom] = useState(1);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setActiveSheet(0);
      setSearchTerm('');
      setZoom(1);
      setDownloadError('');
    }
  }, [open, eventId, filePath]);

  const downloadExcel = async () => {
    if (!excelData || downloadLoading) return;

    setDownloadLoading(true);
    setDownloadError('');
    try {
      const result = await fetchViewerFile({ eventId, filePath, download: true });
      const url = URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename || filename || 'workbook.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setDownloadError(requestError?.message || t('viewer.excel_download_error', 'Unable to download the Excel file'));
    } finally {
      setDownloadLoading(false);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current && containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleSheetChange = (event, newValue) => {
    setActiveSheet(newValue);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const updateZoom = (nextZoom) => {
    const normalizedZoom = Math.min(EXCEL_MAX_ZOOM, Math.max(EXCEL_MIN_ZOOM, nextZoom));
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      zoomAnchorRef.current = {
        contentX: (tableContainer.scrollLeft + (tableContainer.clientWidth / 2)) / zoom,
        contentY: (tableContainer.scrollTop + (tableContainer.clientHeight / 2)) / zoom,
      };
    }
    setZoom(Number(normalizedZoom.toFixed(2)));
  };

  const zoomIn = () => updateZoom(zoom + EXCEL_ZOOM_STEP);
  const zoomOut = () => updateZoom(zoom - EXCEL_ZOOM_STEP);
  const resetZoom = () => updateZoom(1);

  const currentSheet = excelData?.sheets?.[activeSheet] || null;
  const sourceRows = useMemo(() => currentSheet?.data || [], [currentSheet]);
  const stylePalette = currentSheet?.stylePalette || [];
  const headerStyleIds = currentSheet?.headerStyleIds || [];
  const dataStyleIds = currentSheet?.dataStyleIds || [];
  const getCellStyle = (styleId) => (
    Number.isInteger(styleId) && styleId > 0 ? stylePalette[styleId] || {} : {}
  );
  const headers = useMemo(() => (
    currentSheet?.headers?.length
      ? currentSheet.headers
      : (sourceRows[0] || []).map((_, index) => getExcelColumnLabel(index))
  ), [currentSheet, sourceRows]);
  const columnCount = useMemo(
    () => sourceRows.reduce((maxColumns, row) => Math.max(maxColumns, row.length), headers.length),
    [headers.length, sourceRows]
  );
  const filteredRows = useMemo(() => {
    const normalizedSearchTerm = deferredSearchTerm.trim().toLowerCase();
    return sourceRows
      .map((row, sourceIndex) => ({ row, sourceIndex }))
      .filter(({ row }) => !normalizedSearchTerm || row.some(cell => (
        cell !== null
        && cell !== undefined
        && String(cell).toLowerCase().includes(normalizedSearchTerm)
      )));
  }, [deferredSearchTerm, sourceRows]);
  const {
    columnWidth,
    rowHeaderWidth,
    headerHeight,
    rowHeight,
    tableWidth,
  } = useMemo(() => getExcelViewerLayout(zoom, columnCount), [zoom, columnCount]);
  const visibleRowCount = Math.ceil(Math.max(viewportHeight - headerHeight, 0) / rowHeight);
  const firstVisibleRow = Math.max(
    0,
    Math.floor(Math.max(scrollTop - headerHeight, 0) / rowHeight) - EXCEL_OVERSCAN_ROWS
  );
  const lastVisibleRow = Math.min(
    filteredRows.length,
    firstVisibleRow + visibleRowCount + (EXCEL_OVERSCAN_ROWS * 2)
  );
  const visibleRows = filteredRows.slice(firstVisibleRow, lastVisibleRow);

  useWheelZoom({
    containerRef: tableContainerRef,
    enabled: Boolean(open && excelData && !loading),
    maximumZoom: EXCEL_MAX_ZOOM,
    minimumZoom: EXCEL_MIN_ZOOM,
    setZoom,
    zoom,
  });

  useEffect(() => {
    setScrollTop(0);
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [open, activeSheet, deferredSearchTerm]);

  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (!tableContainer) return undefined;

    const updateViewportHeight = () => setViewportHeight(tableContainer.clientHeight);
    updateViewportHeight();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const resizeObserver = new ResizeObserver(updateViewportHeight);
    resizeObserver.observe(tableContainer);
    return () => resizeObserver.disconnect();
  }, [open, excelData, activeSheet]);

  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    const zoomAnchor = zoomAnchorRef.current;
    if (!tableContainer || !zoomAnchor) return undefined;

    const frame = window.requestAnimationFrame(() => {
      tableContainer.scrollLeft = Math.max(
        0,
        (zoomAnchor.contentX * zoom) - (tableContainer.clientWidth / 2)
      );
      tableContainer.scrollTop = Math.max(
        0,
        (zoomAnchor.contentY * zoom) - (tableContainer.clientHeight / 2)
      );
      zoomAnchorRef.current = null;
      setScrollTop(tableContainer.scrollTop);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [zoom]);

  const handleTableScroll = (event) => {
    setScrollTop(event.currentTarget.scrollTop);
  };

  return (
    <ViewerDialogFrame
      embedded={embedded}
      DialogComponent={StyledDialog}
      open={open} 
      onClose={onClose}
      maxWidth={false}
      fullScreen={isFullscreen || isSmallScreen}
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <TableChartIcon sx={{ 
            mr: 1.5, 
            fontSize: '1.4rem',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
          }} />
          <Typography variant="h6" component="div" sx={{ margin: 0 }}>
            {filename ? `${t('viewer.excel_viewer_title')} - ${filename}` : t('viewer.excel_viewer_title')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ 
            color: 'white',
            marginLeft: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <ToolbarContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={downloadExcel} 
            disabled={!excelData || downloadLoading}
            size="small"
            title={t('viewer.download_excel', 'Download Excel')}
          >
            {downloadLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
          </IconButton>
          <IconButton 
            onClick={toggleFullscreen}
            size="small"
            title={isFullscreen
              ? t('viewer.exit_fullscreen', 'Exit fullscreen')
              : t('viewer.fullscreen', 'Fullscreen')}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              ml: 1,
              pl: 1,
              borderLeft: '1px solid rgba(87, 87, 176, 0.15)',
            }}
          >
            <IconButton
              onClick={zoomOut}
              disabled={zoom <= EXCEL_MIN_ZOOM}
              size="small"
              title={t('viewer.excel_zoom_out', 'Zoom out')}
              aria-label={t('viewer.excel_zoom_out', 'Zoom out')}
            >
              <ZoomOutIcon />
            </IconButton>
            <Typography
              variant="caption"
              sx={{ minWidth: 46, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
              aria-live="polite"
            >
              {Math.round(zoom * 100)}%
            </Typography>
            <IconButton
              onClick={zoomIn}
              disabled={zoom >= EXCEL_MAX_ZOOM}
              size="small"
              title={t('viewer.excel_zoom_in', 'Zoom in')}
              aria-label={t('viewer.excel_zoom_in', 'Zoom in')}
            >
              <ZoomInIcon />
            </IconButton>
            <IconButton
              onClick={resetZoom}
              disabled={zoom === 1}
              size="small"
              title={t('viewer.excel_reset_zoom', 'Reset zoom')}
              aria-label={t('viewer.excel_reset_zoom', 'Reset zoom')}
            >
              <RestartAltIcon />
            </IconButton>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {downloadError && (
            <Typography variant="caption" color="error" sx={{ maxWidth: 240 }}>
              {downloadError}
            </Typography>
          )}
          {excelData && currentSheet && (
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {deferredSearchTerm.trim()
                ? t('viewer.excel_filtered_rows', '{{matched}} / {{total}} rows', {
                  matched: filteredRows.length,
                  total: sourceRows.length,
                })
                : t('viewer.excel_row_count', '{{count}} rows', { count: sourceRows.length })}
            </Typography>
          )}
          <TextField
            size="small"
            placeholder={t('viewer.search_placeholder', 'Search...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ width: 200 }}
          />
        </Box>
      </ToolbarContainer>

      <ViewerDialogContent sx={{
        '--dialog-content-top-padding': '4px',
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        minHeight: 0,
        padding: '4px',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}>
        <ExcelContainer ref={containerRef}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              gap: 2
            }}>
              <LinearProgress sx={{ width: 300 }} />
              <Typography color="text.secondary">
                {t('viewer.loading_excel')}
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              padding: 4
            }}>
              <Typography color="error" variant="h6" gutterBottom>
                {t('viewer.error_loading_excel')}
              </Typography>
              <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
                {error}
              </Typography>
            </Box>
          ) : excelData && excelData.sheets && excelData.sheets.length > 0 ? (
            <>
              {/* Sheet tabs */}
              {excelData.sheets.length > 1 && (
                <StyledTabs
                  value={activeSheet}
                  onChange={handleSheetChange}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {excelData.sheets.map((sheet, index) => (
                    <Tab
                      key={index}
                      label={sheet.name || `Sheet ${index + 1}`}
                      value={index}
                    />
                  ))}
                </StyledTabs>
              )}

              {/* Virtualized table content: only rows near the viewport are mounted. */}
              <div 
                ref={tableContainerRef}
                onScroll={handleTableScroll}
                role="grid"
                tabIndex={0}
                aria-label={filename || t('viewer.excel_viewer_title', 'Excel Viewer')}
                aria-rowcount={filteredRows.length + 1}
                aria-colcount={columnCount + 1}
                style={{
                  flex: '1 1 auto',
                  minHeight: 0,
                  overflow: 'auto',
                  overflowX: 'auto',
                  overflowY: 'scroll',
                  scrollbarGutter: 'stable both-edges',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  margin: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  width: 'auto',
                  position: 'relative',
                  boxSizing: 'border-box',
                }}
              >
                <div
                  data-wheel-zoom-anchor="excel-grid"
                  style={{ width: tableWidth }}
                >
                  <div
                    role="row"
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      display: 'grid',
                      gridTemplateColumns: `${rowHeaderWidth}px repeat(${Math.max(columnCount, 1)}, ${columnWidth}px)`,
                      width: tableWidth,
                      height: headerHeight,
                      backgroundColor: '#f8f9fa',
                    }}
                  >
                    <ExcelGridCell
                      header
                      rowHeader
                      width={rowHeaderWidth}
                      height={headerHeight}
                      zoom={zoom}
                      title={t('viewer.excel_row_header', 'Row')}
                    >
                      #
                    </ExcelGridCell>
                    {Array.from({ length: Math.max(columnCount, 1) }, (_, index) => {
                      const header = headers[index] || getExcelColumnLabel(index);
                      return (
                        <ExcelGridCell
                          key={index}
                          header
                          title={header}
                          width={columnWidth}
                          height={headerHeight}
                          zoom={zoom}
                          cellStyle={getCellStyle(headerStyleIds[index])}
                        >
                          {header}
                        </ExcelGridCell>
                      );
                    })}
                  </div>
                  <div
                    style={{
                      position: 'relative',
                      width: tableWidth,
                      height: filteredRows.length * rowHeight,
                    }}
                  >
                    {visibleRows.map(({ row, sourceIndex }, visibleIndex) => {
                      const rowIndex = firstVisibleRow + visibleIndex;
                      const rowNumber = sourceIndex + 2;
                      return (
                        <div
                          key={rowIndex}
                          role="row"
                          style={{
                            position: 'absolute',
                            top: rowIndex * rowHeight,
                            left: 0,
                            display: 'grid',
                            gridTemplateColumns: `${rowHeaderWidth}px repeat(${Math.max(columnCount, 1)}, ${columnWidth}px)`,
                            width: tableWidth,
                            height: rowHeight,
                            backgroundColor: rowIndex % 2 === 1 ? '#fafafa' : 'white',
                          }}
                        >
                          <ExcelGridCell
                            rowHeader
                            width={rowHeaderWidth}
                            height={rowHeight}
                            zoom={zoom}
                            title={`${t('viewer.excel_row_header', 'Row')} ${rowNumber}`}
                          >
                            {rowNumber}
                          </ExcelGridCell>
                          {Array.from({ length: Math.max(columnCount, 1) }, (_, cellIndex) => {
                            const cell = row[cellIndex];
                            const cellText = cell === null || cell === undefined ? '' : String(cell);
                            return (
                              <ExcelGridCell
                                key={cellIndex}
                                title={cellText}
                                width={columnWidth}
                                height={rowHeight}
                                zoom={zoom}
                                cellStyle={getCellStyle(dataStyleIds[sourceIndex]?.[cellIndex])}
                              >
                                {cellText}
                              </ExcelGridCell>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              padding: 4
            }}>
              <Typography color="text.secondary">
                {t('viewer.no_excel_content')}
              </Typography>
            </Box>
          )}
        </ExcelContainer>
      </ViewerDialogContent>
    </ViewerDialogFrame>
  );
};

export default ExcelViewer;
